import React, { useState } from "react";
import api from "../api";
import { useT, useLang } from "../i18n";
import safeStorage from "../lib/safeStorage";
import { tx as __tx } from "../lib/tx";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // "login" | "register"
  const t = useT();
  const { lang, switchLang } = useLang();
  const isRegister = mode === "register";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    if (isRegister && password.length < 8) {
      setError(t.login.passwordMinHint || __tx("min 8 characters"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const path = isRegister ? "/auth/register" : "/auth/login";
      const res = await api.post(path, {
        email: email.trim(),
        password,
      });
      safeStorage.setItem("xpnsr_seo_token", res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || t.login.connectError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="xp-app xp-app-auth">
      <div className="orb orb-1" aria-hidden="true" />
      <div className="orb orb-2" aria-hidden="true" />

      {/* Sidebar */}
      <aside className="xp-sidebar" aria-label={__tx("Primary navigation")}>
        <div className="xp-brand">
          <span className="xp-brand-dot" />
          <span className="xp-brand-logo">xpnsr.</span>
        </div>

        <div className="xp-switcher">
          <div className="xp-section-label">{t.nav.product}</div>
          <div className="xp-sw">
            <a className="xp-sw-btn" href="https://app.xpnsr.tech" data-p="xp">
              <span className="xp-sw-ind" style={{ background: "#00e5a0" }} />
              <span>{__tx("Expense")}</span>
              <span className="xp-sw-kbd">⌘1</span>
            </a>
            <button type="button" className="xp-sw-btn on" data-p="seo" aria-current="page">
              <span className="xp-sw-ind" style={{ background: "#0076ff" }} />
              <span>CBN</span>
              <span className="xp-sw-kbd">⌘2</span>
            </button>
            <a className="xp-sw-btn" href="https://tracker.xpnsr.tech" data-p="trk">
              <span className="xp-sw-ind" style={{ background: "#f97316" }} />
              <span>{__tx("Tracker")}</span>
              <span className="xp-sw-kbd">⌘3</span>
            </a>
          </div>
        </div>

        <div className="xp-nav-group">
          <h6>{t.nav.navigate}</h6>
          <a className="xp-nav-link" href="https://xpnsr.tech">{__tx("Home")}</a>
          <a className="xp-nav-link on" href="#auth">{__tx("Auth")}</a>
        </div>

        <a
          href="https://xpnsr.tech/book-demo.html"
          target="_blank"
          rel="noreferrer noopener"
          className="xp-sidebar-cta"
        >
          {t.nav.bookDemo}
        </a>

        <div className="xp-sidebar-footer">
          <div className="xp-foot-row">
            <div className="xp-lang" role="group" aria-label={__tx("Language")}>
              {["en", "ru"].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => switchLang(l)}
                  className={`xp-lang-btn ${lang === l ? "on" : ""}`}
                  aria-pressed={lang === l}
                  aria-label={l === "en" ? "English" : __tx("Русский")}
                >
                  {l === "en" ? "EN" : "RU"}
                </button>
              ))}
            </div>
          </div>
        </div>
      </aside>

      <main className="xp-main">
        <div className="xp-top">
          <div className="xp-crumb">
            <span>{__tx("xpnsr")}</span>
            <span className="xp-crumb-sep">/</span>
            <b>SEO</b>
            <span className="xp-crumb-sep">/</span>
            <span>{__tx("auth")}</span>
          </div>
          <div className="xp-pulse" style={{ marginLeft: "auto" }}>
            <span className="xp-pulse-dot" aria-hidden="true" />
            <span>{t.top?.live || "live"}</span>
          </div>
        </div>

        <div className="xp-auth-wrap">
          <div className="xp-auth-card" key={lang}>
            <div className="xp-chrome">
              <div className="xp-chrome-dots">
                <i></i>
                <i></i>
                <i></i>
              </div>
              <div className="xp-chrome-path">{__tx("auth.xpnsr.tech/seo")}</div>
              <div className="xp-chrome-tls">{__tx("TLS 1.3")}</div>
            </div>

            <div className="xp-card-body">
              <div className="xp-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isRegister}
                  className={!isRegister ? "on" : ""}
                  onClick={() => { setMode("login"); setError(""); }}
                >
                  {t.login.tabSignIn || t.login.heading}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isRegister}
                  className={isRegister ? "on" : ""}
                  onClick={() => { setMode("register"); setError(""); }}
                >
                  {t.login.tabRegister || t.login.registerHeading}
                </button>
              </div>

              <h1 className="xp-h1">
                {isRegister ? (
                  <>
                    {t.login.heroPrefixRegister || __tx("Create your")}{" "}
                    <em>{t.login.heroAccent || __tx("account")}.</em>
                  </>
                ) : (
                  <>
                    Rank <em>higher.</em>
                  </>
                )}
              </h1>
              <p className="xp-auth-sub">
                {t.login.heroSub || __tx("// CBN · rank · backlinks · content network")}
              </p>

              {error && (
                <div className="xp-alert" role="alert" aria-live="polite">
                  <span aria-hidden="true">⚠</span> {error}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                noValidate
                aria-label={isRegister ? "Registration form" : "Login form"}
              >
                <div className="xp-f">
                  <label htmlFor="login-email">{t.login.email}</label>
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.login.emailHolder}
                    autoComplete="email"
                    spellCheck={false}
                    required
                  />
                </div>

                <div className="xp-f">
                  <label htmlFor="login-password">{t.login.password}</label>
                  <div style={{ position: "relative" }}>
                    <input
                      id="login-password"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t.login.passHolder}
                      autoComplete={isRegister ? "new-password" : "current-password"}
                      required
                      minLength={isRegister ? 8 : undefined}
                      aria-describedby={isRegister ? "password-hint" : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        color: "var(--xp-muted)",
                        cursor: "pointer",
                        fontFamily: "var(--xp-mono)",
                        fontSize: "10px",
                        letterSpacing: "0.1em",
                      }}
                    >
                      {showPw ? "HIDE" : "SHOW"}
                    </button>
                  </div>
                  {isRegister && (
                    <span id="password-hint" className="sr-only">
                      {t.login.passwordMinHint || __tx("min 8 characters")}
                    </span>
                  )}
                  {!isRegister && (
                    <a
                      href="mailto:support@xpnsr.tech?subject=Password%20reset"
                      className="xp-forgot"
                      data-testid="forgot-password-link"
                    >
                      {t.login.forgotPassword || __tx("Forgot password?")}
                    </a>
                  )}
                </div>

                <button
                  type="submit"
                  className="xp-btn-main"
                  disabled={loading || !email.trim() || !password}
                >
                  {loading
                    ? isRegister
                      ? t.login.registerSubmitting || t.login.submitting
                      : t.login.submitting
                    : isRegister
                      ? t.login.registerSubmit || t.login.submit
                      : `${t.login.submit} →`}
                </button>

                <button
                  type="button"
                  className="xp-btn-ghost"
                  onClick={() => {
                    setMode(isRegister ? "login" : "register");
                    setError("");
                  }}
                >
                  {isRegister
                    ? t.login.switchToLogin || __tx("Have an account? Sign in")
                    : t.login.switchToRegister || __tx("No account? Register")}
                </button>
              </form>

              <div className="xp-status">
                <div className="sr">
                  <b>{t.login.statusSession || __tx("session")}</b>&nbsp;
                  {t.login.statusSessionVal || __tx("encrypted · TLS 1.3")}
                </div>
                <div className="sr">
                  <b>{t.login.statusDataforseo || __tx("dataforseo")}</b>&nbsp;
                  {t.login.statusDataforseoVal || __tx("api · connected")}
                </div>
                <div className="sr">
                  <b>{t.login.statusDeepseek || __tx("deepseek")}</b>&nbsp;
                  {t.login.statusDeepseekVal || __tx("cluster · ready")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
